import { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

function FilterChip({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`
        px-3 py-1.5 text-sm font-medium rounded-full transition-colors
        ${active
          ? 'bg-primary-100 text-primary-700 ring-1 ring-primary-300'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }
      `}
    >
      {label}
    </button>
  )
}

function FilterDropdown({ label, value, options, onChange, placeholder }) {
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value || null)}
      aria-label={label}
      className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 focus:ring-primary-500 focus:border-primary-500"
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  )
}

function ProductFilters({ filters, onChange, availableBrands = [], availableCategories = [] }) {
  const { t } = useTranslation('ecommerce')
  const [showAdvanced, setShowAdvanced] = useState(false)

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (filters.is_featured === true) count++
    if (filters.has_discount === true) count++
    if (filters.inventory_status === 'in_stock') count++
    if (filters.has_rating === true) count++
    if (filters.min_rating) count++
    if (filters.max_stock) count++
    if (filters.brand) count++
    if (filters.category) count++
    return count
  }, [filters])

  const handleSearchChange = useCallback(
    (e) => {
      onChange({ ...filters, search: e.target.value })
    },
    [filters, onChange]
  )

  const toggleFilter = useCallback(
    (key, value) => {
      const currentValue = filters[key]
      onChange({
        ...filters,
        [key]: currentValue === value ? null : value,
      })
    },
    [filters, onChange]
  )

  const updateFilter = useCallback(
    (key, value) => {
      onChange({
        ...filters,
        [key]: value,
      })
    },
    [filters, onChange]
  )

  const clearFilters = useCallback(() => {
    onChange({
      search: '',
      is_featured: null,
      inventory_status: null,
      has_discount: null,
      has_rating: null,
      min_rating: null,
      max_stock: null,
      brand: null,
      category: null,
    })
  }, [onChange])

  const ratingOptions = [
    { value: '3.5', label: '3.5+' },
    { value: '4.0', label: '4.0+' },
    { value: '4.5', label: '4.5+' },
  ]

  const stockOptions = [
    { value: '5', label: t('products.filters.lowStock5', '5 or less') },
    { value: '10', label: t('products.filters.lowStock10', '10 or less') },
    { value: '20', label: t('products.filters.lowStock20', '20 or less') },
  ]

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg
            className="h-5 w-5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <input
          type="text"
          value={filters.search}
          onChange={handleSearchChange}
          placeholder={t('products.filters.search', 'Search products...')}
          aria-label={t('products.filters.search', 'Search products...')}
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Primary Filter Chips */}
      <div className="flex flex-wrap items-center gap-2">
        <FilterChip
          label={t('products.filters.featured', 'Featured')}
          active={filters.is_featured === true}
          onClick={() => toggleFilter('is_featured', true)}
        />
        <FilterChip
          label={t('products.filters.onSale', 'On Sale')}
          active={filters.has_discount === true}
          onClick={() => toggleFilter('has_discount', true)}
        />
        <FilterChip
          label={t('products.filters.inStock', 'In Stock')}
          active={filters.inventory_status === 'in_stock'}
          onClick={() => toggleFilter('inventory_status', 'in_stock')}
        />
        <FilterChip
          label={t('products.filters.withRatings', 'With Ratings')}
          active={filters.has_rating === true}
          onClick={() => toggleFilter('has_rating', true)}
        />

        {/* Divider */}
        <div className="h-6 w-px bg-gray-300 mx-1" />

        {/* Advanced toggle */}
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
        >
          <svg
            className={`h-4 w-4 transform transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          {t('products.filters.advanced', 'More Filters')}
        </button>

        {activeFilterCount > 0 && (
          <>
            <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-primary-600 rounded-full">
              {activeFilterCount}
            </span>
            <button
              type="button"
              onClick={clearFilters}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              {t('products.filters.clear', 'Clear all')}
            </button>
          </>
        )}
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-gray-200">
          {/* Min Rating */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">{t('products.filters.minRating', 'Min Rating')}:</span>
            <FilterDropdown
              label={t('products.filters.minRating', 'Min Rating')}
              value={filters.min_rating}
              options={ratingOptions}
              onChange={(val) => updateFilter('min_rating', val ? parseFloat(val) : null)}
              placeholder={t('products.filters.anyRating', 'Any')}
            />
          </div>

          {/* Low Stock (Urgency) */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">{t('products.filters.lowStock', 'Low Stock')}:</span>
            <FilterDropdown
              label={t('products.filters.lowStock', 'Low Stock')}
              value={filters.max_stock}
              options={stockOptions}
              onChange={(val) => updateFilter('max_stock', val ? parseInt(val, 10) : null)}
              placeholder={t('products.filters.anyStock', 'Any')}
            />
          </div>

          {/* Brand */}
          {availableBrands.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">{t('products.filters.brand', 'Brand')}:</span>
              <FilterDropdown
                label={t('products.filters.brand', 'Brand')}
                value={filters.brand}
                options={availableBrands.map((b) => ({ value: b, label: b }))}
                onChange={(val) => updateFilter('brand', val)}
                placeholder={t('products.filters.allBrands', 'All')}
              />
            </div>
          )}

          {/* Category */}
          {availableCategories.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">{t('products.filters.category', 'Category')}:</span>
              <FilterDropdown
                label={t('products.filters.category', 'Category')}
                value={filters.category}
                options={availableCategories.map((c) => ({ value: c, label: c }))}
                onChange={(val) => updateFilter('category', val)}
                placeholder={t('products.filters.allCategories', 'All')}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

FilterChip.propTypes = {
  label: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
}

FilterDropdown.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
}

ProductFilters.propTypes = {
  filters: PropTypes.shape({
    search: PropTypes.string,
    is_featured: PropTypes.bool,
    inventory_status: PropTypes.string,
    has_discount: PropTypes.bool,
    has_rating: PropTypes.bool,
    min_rating: PropTypes.number,
    max_stock: PropTypes.number,
    brand: PropTypes.string,
    category: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  availableBrands: PropTypes.arrayOf(PropTypes.string),
  availableCategories: PropTypes.arrayOf(PropTypes.string),
}

export default ProductFilters
